import React, { useState, useEffect } from 'react';
import { Box, Button, Menu, MenuItem, Typography, useTheme, useMediaQuery } from '@mui/material';
import { Language as LanguageIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useLanguage } from '@/store/modules/languageStore';
import { useTranslation } from 'react-i18next';

interface Country {
    code: string;
    name: string;
    flag: string;
}

const countries: Country[] = [
    { code: 'KR', name: '한국어', flag: '🇰🇷' },
    { code: 'US', name: 'English', flag: '🇺🇸' },
    { code: 'CN', name: '简体中文', flag: '🇨🇳' },
    { code: 'TW', name: '繁體中文', flag: '🇹🇼' },
    { code: 'JP', name: '日本語', flag: '🇯🇵' },
    { code: 'VN', name: 'Tiếng Việt', flag: '🇻🇳' },
];

interface CountrySelectorProps {
    selectedCountry?: string;
    onCountryChange?: (countryCode: string) => void;
    sx?: any;
}

const CountrySelector: React.FC<CountrySelectorProps> = ({ selectedCountry, onCountryChange, sx = {} }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { t } = useTranslation();
    const { getCurrentCountryCode, setLanguageByCountryCode } = useLanguage();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selected, setSelected] = useState(selectedCountry || getCurrentCountryCode());

    // 언어 스토어와 동기화
    useEffect(() => {
        const currentCountryCode = getCurrentCountryCode();
        setSelected(currentCountryCode);
    }, [getCurrentCountryCode]);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleCountrySelect = (countryCode: string) => {
        setSelected(countryCode);
        setLanguageByCountryCode(countryCode);
        onCountryChange?.(countryCode);
        handleClose();
    };

    const selectedCountryData = countries.find((country) => country.code === selected) || countries[0];

    return (
        <Box sx={{ ...sx }}>
            <Button
                onClick={handleClick}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1.5,
                    py: 0.8,
                    minWidth: 'auto',
                    color: '#666',
                    fontSize: isMobile ? 13 : 14,
                    fontWeight: 500,
                    textTransform: 'none',
                    backgroundColor: '#f8f8f8',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0',
                    '&:hover': {
                        backgroundColor: '#f0f0f0',
                        borderColor: '#d0d0d0',
                    },
                    '&:focus': {
                        backgroundColor: '#f0f0f0',
                        borderColor: '#d0d0d0',
                    },
                }}
            >
                <LanguageIcon sx={{ fontSize: 18, color: '#666' }} />
                <Typography
                    variant="body2"
                    sx={{
                        color: '#222',
                        fontWeight: 500,
                        fontSize: isMobile ? 13 : 14,
                    }}
                >
                    {selectedCountryData.name}
                </Typography>
                <ExpandMoreIcon
                    sx={{
                        fontSize: 16,
                        color: '#666',
                        transform: anchorEl ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease-in-out',
                    }}
                />
            </Button>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        mt: 1,
                        minWidth: 200,
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                        border: '1px solid #e0e0e0',
                        overflow: 'hidden',
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {countries.map((country) => (
                    <MenuItem
                        key={country.code}
                        onClick={() => handleCountrySelect(country.code)}
                        sx={{
                            px: 2,
                            py: 1.2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            backgroundColor: selected === country.code ? '#fff5f5' : 'transparent',
                            color: selected === country.code ? '#FF0033' : '#222',
                            fontWeight: selected === country.code ? 600 : 400,
                            fontSize: 14,
                            '&:hover': {
                                backgroundColor: selected === country.code ? '#fff5f5' : '#f8f8f8',
                            },
                            '&:focus': {
                                backgroundColor: selected === country.code ? '#fff5f5' : '#f8f8f8',
                            },
                            borderBottom: '1px solid #f0f0f0',
                            '&:last-child': {
                                borderBottom: 'none',
                            },
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                fontSize: 18,
                                lineHeight: 1,
                            }}
                        >
                            {country.flag}
                        </Box>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'inherit',
                                fontWeight: 'inherit',
                                fontSize: 'inherit',
                            }}
                        >
                            {country.name}
                        </Typography>
                        {selected === country.code && (
                            <Box
                                sx={{
                                    ml: 'auto',
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    backgroundColor: '#FF0033',
                                }}
                            />
                        )}
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};

export default CountrySelector;
